
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const testimonials = [
  {
    id: 1,
    name: 'María García',
    location: 'Llefià, Badalona',
    text: 'Necesitábamos dividir el salón para crear un despacho y el resultado ha sido impecable. Trabajo limpio, rápido y el acabado listo para pintar. Muy profesionales.',
    project: 'Tabique divisorio',
    initials: 'MG',
  },
  {
    id: 2,
    name: 'Carlos Fernández',
    location: 'Centre, Badalona',
    text: 'Instalaron el falso techo en nuestra oficina en tiempo récord. La coordinación fue excelente y apenas notamos molestias durante la obra. Totalmente recomendables.',
    project: 'Falso techo registrable',
    initials: 'CF',
  },
  {
    id: 3,
    name: 'Ana Martínez',
    location: 'Montigalà, Badalona',
    text: 'El aislamiento acústico que nos pusieron ha cambiado nuestra calidad de vida. Ya no escuchamos a los vecinos. Presupuesto claro y sin sorpresas.',
    project: 'Aislamiento acústico',
    initials: 'AM',
  },
  {
    id: 4,
    name: 'Jordi Puig',
    location: 'Gorg, Badalona',
    text: 'Reforma completa del baño con pladur antihumedad. Quedó perfecto y el equipo dejó todo limpio al terminar. Muy contentos con el resultado final.',
    project: 'Pladur antihumedad',
    initials: 'JP',
  },
];

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section ref={ref} className="py-24 bg-dark-card">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6"
          >
            <span className="text-orange-primary text-sm font-bold">Testimonios</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl font-extrabold text-text-primary mb-6"
          >
            Lo que dicen nuestros clientes
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-text-secondary text-lg max-w-2xl mx-auto"
          >
            La satisfacción de nuestros clientes es nuestra mejor carta de presentación
          </motion.p>
        </div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative"
        >
          <div className="overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="bg-dark-bg border border-dark-border rounded-2xl p-8 md:p-12"
              >
                <div className="flex flex-col items-center text-center">
                  {/* Quote Icon */}
                  <div className="w-12 h-12 flex items-center justify-center mb-6">
                    <i className="ri-double-quotes-l text-orange-primary text-4xl"></i>
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-text-primary text-lg md:text-xl leading-relaxed mb-8 max-w-3xl">
                    {testimonials[currentIndex].text}
                  </p>

                  {/* Client Info */}
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-primary to-orange-secondary rounded-full flex items-center justify-center mb-4">
                      <span className="text-white font-bold text-lg">
                        {testimonials[currentIndex].initials}
                      </span>
                    </div>
                    <p className="text-text-primary font-bold text-lg">
                      {testimonials[currentIndex].name}
                    </p>
                    <p className="text-text-muted text-sm mb-2">
                      {testimonials[currentIndex].location}
                    </p>
                    <span className="px-3 py-1 bg-orange-primary/10 text-orange-primary text-xs font-medium rounded-full">
                      {testimonials[currentIndex].project}
                    </span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-12 h-12 bg-dark-card border border-dark-border rounded-full flex items-center justify-center text-text-secondary hover:text-orange-primary hover:border-orange-primary transition-all duration-300 cursor-pointer"
            aria-label="Testimonio anterior"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-12 h-12 bg-dark-card border border-dark-border rounded-full flex items-center justify-center text-text-secondary hover:text-orange-primary hover:border-orange-primary transition-all duration-300 cursor-pointer"
            aria-label="Siguiente testimonio"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentIndex
                    ? 'bg-orange-primary w-8'
                    : 'bg-dark-border hover:bg-text-muted'
                }`}
                aria-label={`Ir al testimonio ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
